import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import Loading from '@ingka/loading';
import Text from '@ingka/text';
import { getMe } from '@/api/client';
import { useApp } from '@/context/AppContext';
import '@ingka/inline-message/dist/style.css';
import { useEffect, useState } from 'react';
import {
  type CatalogClassificationAdmin,
  createAdminClassification,
  listAdminClassifications,
  updateAdminClassification,
} from '@/api/client';
import CatalogItemModal from '@/components/admin/CatalogItemModal';
import styles from './AdminCatalog.module.css';

type ModalState =
  | { open: false }
  | { open: true; mode: 'create' }
  | { open: true; mode: 'edit'; classification: CatalogClassificationAdmin };

export default function AdminClassificationsPage() {
  const [items, setItems] = useState<CatalogClassificationAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ open: false });
  const { setUser } = useApp();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { classifications } = await listAdminClassifications();
      setItems(classifications);
      const me = await getMe();
      setUser(me);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load classifications');
    } finally {
      setLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is unmemoized and intended to run once on mount; adding it would re-fetch on every render.
  useEffect(() => {
    load();
  }, []);

  const handleSave = async (data: {
    id: string;
    name: string;
    description: string;
    active?: boolean;
  }) => {
    if (modal.open && modal.mode === 'create') {
      await createAdminClassification({
        id: data.id,
        name: data.name,
        description: data.description || null,
      });
    } else if (modal.open && modal.mode === 'edit') {
      await updateAdminClassification(modal.classification.id, {
        name: data.name,
        description: data.description || null,
        active: data.active,
      });
    }
    setModal({ open: false });
    await load();
  };

  return (
    <>
      <div className={styles.header}>
        <div>
          <Text tagName="h1" textStyle="Heading.L">
            Classifications
          </Text>
          <Text tagName="p" textStyle="Body.M" className={styles.subtitle}>
            Manage classification levels available in the upload wizard.
          </Text>
        </div>
        <Button
          type="emphasised"
          text="Add classification"
          onClick={() => setModal({ open: true, mode: 'create' })}
        />
      </div>

      {error && (
        <div className={styles.errorWrap}>
          <InlineMessage variant="negative" title="Error" body={error} />
        </div>
      )}

      {loading ? (
        <div className={styles.loadingWrap}>
          <Loading text="Loading classifications..." />
        </div>
      ) : (
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                <th className={styles.th}>Name</th>
                <th className={styles.th}>ID</th>
                <th className={styles.th}>Description</th>
                <th className={styles.th}>Active</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className={styles.tr}>
                  <td className={styles.td}>{c.name}</td>
                  <td className={styles.td}>
                    <code className={styles.idCell}>{c.id}</code>
                  </td>
                  <td className={styles.td}>{c.description || '—'}</td>
                  <td className={styles.td}>
                    {c.active ? (
                      <span className={styles.activeOn}>●</span>
                    ) : (
                      <span className={styles.activeOff}>●</span>
                    )}
                  </td>
                  <td className={styles.tdRight}>
                    <Button
                      type="secondary"
                      size="small"
                      text="Edit"
                      onClick={() => setModal({ open: true, mode: 'edit', classification: c })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal.open && (
        <CatalogItemModal
          title={modal.mode === 'create' ? 'Add classification' : 'Edit classification'}
          mode={modal.mode}
          initial={
            modal.mode === 'edit'
              ? {
                  id: modal.classification.id,
                  name: modal.classification.name,
                  description: modal.classification.description ?? '',
                  active: modal.classification.active,
                }
              : undefined
          }
          warningBody="Adding a classification affects how uploads are routed downstream. Coordinate with the data team."
          onClose={() => setModal({ open: false })}
          onSave={handleSave}
        />
      )}
    </>
  );
}
